import { useSelector, useDispatch } from 'react-redux';
import { useCallback } from 'react';
import {
  selectMetadataMappingStatus,
  selectMetadataMappingError,
  setMetadataMappingLoading,
  setMetadataMappingError,
  clearMetadataMappingState,
  selectMetadataMappingMessage,
} from '../../../../reducers/metadataMappingReducer';
import { useAiAgentApiFacade } from '../../../../apis/facades/useAiAgentApiFacade';
import { AiChatMessage } from '../../../common/ai-chat/aiChatTypes';
import { ChatCompletionRequestMessageRole } from '@server/ai';
import { DateTime } from 'luxon';
import { telemetry } from '../../../../telemetry';
import useSendBiEvent from '../../../../hooks/useSendBiEvent';
import { AI_AGENTS_EVENTS } from '../../../../services/events';
import { useFunnelMapApiFacade } from '../../../../apis/facades/funnel-map/useFunnelMapApiFacade';
import { setFunnelMap } from '../../../../reducers/united-canvas/unitedCanvasReducer';
import { normalizeFunnelMapPositions } from '../../funnel-map-flow/normalizeFunnelMapPositions';
import { useAutoSaveContext } from '../../funnel-map-flow/auto-save/AutoSaveContext';
import { setIsPageLoading } from '../../../../reducers/canvasPageStateReducer';

export const useMetadataMapping = (funnelMapId: string) => {
  const dispatch = useDispatch();
  const { post_processMining } = useAiAgentApiFacade();
  const { get_funnelMap } = useFunnelMapApiFacade();
  const { funnelMapVersion } = useAutoSaveContext();
  const sendBiEvent = useSendBiEvent();

  const status = useSelector(selectMetadataMappingStatus(funnelMapId));
  const error = useSelector(selectMetadataMappingError(funnelMapId));
  const message = useSelector(selectMetadataMappingMessage(funnelMapId));
  const isLoading = status === 'loading' || status === 'completedFetchingData';

  const reset = useCallback(() => {
    dispatch(clearMetadataMappingState({ funnelMapId }));
  }, [dispatch, funnelMapId]);

  const sendMessage = useCallback(
    async (messageContent: string) => {
      if (!funnelMapId) {
        return;
      }
      sendBiEvent({ name: AI_AGENTS_EVENTS.generateProcessMiningFromCanvas });

      const message: AiChatMessage = {
        role: ChatCompletionRequestMessageRole.USER,
        content: messageContent,
        createdAt: DateTime.now().toISO(),
      };

      try {
        dispatch(setMetadataMappingLoading({ funnelMapId, isLoading: true }));
        await post_processMining(funnelMapId, { message });
      } catch (error) {
        telemetry.captureError(error);
        dispatch(setMetadataMappingError({ funnelMapId, error: `Couldn't generate results. Try again with a new prompt.` }));
      }
    },
    [funnelMapId, sendBiEvent, dispatch, post_processMining],
  );

  const complete = useCallback(async () => {
    if (!funnelMapId) return;

    try {
      const funnelMap = (await get_funnelMap(funnelMapId, true)) as FunnelMap;
      funnelMapVersion.current = funnelMap.version;
      dispatch(setFunnelMap(normalizeFunnelMapPositions(funnelMap)));
      dispatch(setIsPageLoading({ isPageLoading: false }));
      dispatch(clearMetadataMappingState({ funnelMapId }));
    } catch (error) {
      telemetry.captureError(error);
      dispatch(clearMetadataMappingState({ funnelMapId }));
    }
  }, [dispatch, funnelMapId, get_funnelMap, funnelMapVersion]);

  return {
    status,
    error,
    message,
    isLoading,
    sendMessage,
    complete,
    reset,
  };
};
